import './styles.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { Modal, Spinner } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import {
  analyzeCigarWrapper,
  enrichCigarHypothesis,
  findSimilarCigarsDebug,
  fingerprintCigarImage,
  hydrateCigarCandidates,
  hydrateSimilarityCandidates,
  identifyCigarDebug,
  type IdentifyResearchMode,
  type IdentifyResearchProfile,
  preprocessCigarImage,
  recognizeCigarFingerprint,
  recognizeCigarText,
} from '@/api/cigar-matching';
import Button from '@/components/overrides/button';
import { getBrands, postCigar } from '@/utils/actions/cigar';
import { uploadToS3 } from '@/utils/imageUtils';

export type MatchFlowSourceType =
  | 'match_request'
  | 'recognition_test'
  | 'upload';

export interface MatchFlowSource {
  image: string;
  originalImage?: string;
  sourceType: MatchFlowSourceType;
  sourceId?: string | number;
  existingMatch?: unknown;
}

export interface MatchFlowAcceptance {
  candidate: any;
  cigarId: string | number;
  step: string;
  confidence: string | null;
}

interface MatchFlowModalProps {
  isOpen: boolean;
  source: MatchFlowSource | null;
  onClose: () => void;
  onAccept?: (acceptance: MatchFlowAcceptance) => void | Promise<void>;
  acceptButtonLabel?: string;
}

type StepKey =
  | 'image'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';
type StepStatus = 'idle' | 'processing' | 'complete' | 'skipped' | 'failed';
type StageState = {
  status: StepStatus;
  result?: any;
  error?: string;
};

type IdentifyStageKey =
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'recognize'
  | 'analyze';
type HypothesisImportState = {
  status: 'importing' | 'complete' | 'failed';
  cigar?: any;
  error?: string;
};
type BrandOption = {
  value: string | number;
  label: string;
  __isNew__?: boolean;
};
type EnrichmentPreview = {
  index: number;
  hypothesis: any;
  sourceUrl: string;
  data: Record<string, any>;
  brand: BrandOption | null;
};
type WrapperAnalysisSourceStep =
  | 'similar'
  | 'text'
  | 'fingerprintRecognize'
  | 'identify';
type WrapperAnalysisState = {
  key: string;
  imageUrl: string;
  sourceStep: WrapperAnalysisSourceStep;
  candidateIds: string[];
  result?: any;
  status: 'processing' | 'complete' | 'failed';
  error?: string;
  updatedAt: number;
};

const EMPTY_IDENTIFY_SELECTION = (): Record<IdentifyStageKey, boolean> => ({
  similar: true,
  text: true,
  fingerprint: true,
  recognize: true,
  analyze: true,
});

const rawStageResult = (result: any) => {
  if (!result) return undefined;
  const rawResult = { ...result };
  delete rawResult.hydratedCandidates;
  delete rawResult.hydratedResearchCandidates;
  return rawResult;
};

const formatEvidenceValue = (value: unknown) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const textCandidates = (result: any): any[] =>
  result?.textCandidates || result?.text_candidates || [];

const candidateCigarId = (candidate: any) =>
  candidate?.cigarId ?? candidate?.cigar_id ?? candidate?.id;

const wrapperCandidateId = (candidate: any) =>
  String(candidateCigarId(candidate) ?? candidate?.recognitionId ?? '');

const numericCandidateScore = (candidate: any) => {
  const score =
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity;
  return Number.isFinite(Number(score)) ? Number(score) : null;
};

const getTiedCandidates = (candidateList: any[]) => {
  const scoreGroups = new Map<number, any[]>();
  candidateList.forEach((candidate) => {
    const score = numericCandidateScore(candidate);
    if (score === null || !wrapperCandidateId(candidate)) return;
    scoreGroups.set(score, [...(scoreGroups.get(score) || []), candidate]);
  });
  return [...scoreGroups.values()].find((group) => group.length >= 2) || [];
};

const wrapperAnalysisKey = (candidateList: any[], imageUrl: string) =>
  `${imageUrl}::${candidateList.map(wrapperCandidateId).sort().join('|')}`;

const STEPS: Array<{
  key: StepKey;
  label: string;
  cost: string;
}> = [
  { key: 'image', label: 'Image', cost: 'Source' },
  { key: 'preprocess', label: 'Isolate', cost: 'Free' },
  { key: 'similar', label: 'Similar', cost: 'Free · Embeddings' },
  { key: 'text', label: 'Text', cost: 'Paid · Vision' },
  { key: 'fingerprint', label: 'Fingerprint', cost: 'Paid · Vision' },
  {
    key: 'fingerprintRecognition',
    label: 'Recognize',
    cost: 'Free · Database',
  },
  { key: 'identify', label: 'Identify', cost: 'Paid · AI' },
];

const EMPTY_STAGES = (): Record<StepKey, StageState> => ({
  image: { status: 'complete' },
  preprocess: { status: 'idle' },
  similar: { status: 'idle' },
  text: { status: 'idle' },
  fingerprint: { status: 'idle' },
  fingerprintRecognition: { status: 'idle' },
  identify: { status: 'idle' },
});

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'This recognition stage failed.';

const percentage = (value: unknown) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${(number <= 1 ? number * 100 : number).toFixed(1)}%`;
};

const duration = (result: any) => {
  const milliseconds =
    result?.meta?.durationMs ??
    result?.metadata?.processing_ms ??
    result?.processing_ms;
  if (!Number.isFinite(Number(milliseconds))) return null;
  return Number(milliseconds) >= 1000
    ? `${(Number(milliseconds) / 1000).toFixed(1)} sec`
    : `${Number(milliseconds)} ms`;
};

const tokenUsage = (result: any) =>
  result?.meta?.tokenUsage ||
  result?.meta?.token_usage ||
  result?.metadata?.tokenUsage ||
  result?.metadata?.token_usage;

const estimatedCost = (result: any) => {
  const value =
    result?.meta?.estimatedCostUsd ??
    result?.meta?.estimated_cost_usd ??
    result?.metadata?.estimatedCostUsd ??
    result?.metadata?.estimated_cost_usd;
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const formatCost = (value: number) =>
  value > 0 && value < 0.0001 ? `$${value.toFixed(6)}` : `$${value.toFixed(4)}`;

const uploadPreprocessedImage = async (image: string) => {
  if (!image.startsWith('data:')) return image;
  const response = await fetch(image);
  const blob = await response.blob();
  const id = uuidv4();
  const upload = await uploadToS3(
    blob,
    'cigars/match-debugger',
    `${id}-preprocessed.jpg`,
    id
  );
  return upload.data.media_url;
};

const values = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: any) => item?.label || item?.name || String(item))
      .filter(Boolean);
  }
  return [String(value)];
};

const candidateLabel = (candidate: any) =>
  candidate?.cigarName ||
  candidate?.label ||
  candidate?.full_name ||
  candidate?.name ||
  candidate?.cigar?.full_name ||
  (candidate?.cigar_id || candidate?.cigarId
    ? `Cigar ${candidate.cigar_id || candidate.cigarId}`
    : 'Candidate');

const candidateScore = (candidate: any) =>
  percentage(
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity
  );

const candidateImage = (candidate: any) =>
  candidate?.bandImageUrl ||
  candidate?.band_image_url ||
  candidate?.displayImageUrl ||
  candidate?.display_image_url ||
  candidate?.cigarImageUrl ||
  candidate?.imageUrl ||
  candidate?.image_url ||
  candidate?.imageId;

const candidateBandImage = (candidate: any) =>
  candidate?.cigarBandImageUrl ||
  candidate?.bandImageUrl ||
  candidate?.band_image_url;

const identificationCandidates = (result: any) => {
  const ranked = [
    ...(result?.match ? [{ ...result.match, resultType: 'match' }] : []),
    ...(result?.suggestions || []).map((candidate: any) => ({
      ...candidate,
      resultType: 'suggestion',
    })),
    ...(result?.consensus?.candidates || []).map((candidate: any) => ({
      ...candidate,
      resultType: 'consensus',
    })),
  ];
  const merged: any[] = [];
  const candidateIndexes = new Map<string, number>();

  ranked.forEach((candidate, index) => {
    const id = candidate.cigar_id ?? candidate.cigarId;
    const key =
      id === undefined || id === null ? `candidate-${index}` : `${id}`;
    const existingIndex = candidateIndexes.get(key);

    if (existingIndex === undefined) {
      candidateIndexes.set(key, merged.length);
      merged.push(candidate);
      return;
    }

    const existing = merged[existingIndex];
    const evidence = [
      ...(Array.isArray(existing.evidence) ? existing.evidence : []),
      ...(Array.isArray(candidate.evidence) ? candidate.evidence : []),
    ].filter((item, evidenceIndex, all) => all.indexOf(item) === evidenceIndex);
    merged[existingIndex] = {
      ...candidate,
      ...existing,
      stageScores: {
        ...(candidate.stageScores || {}),
        ...(existing.stageScores || {}),
      },
      ...(evidence.length ? { evidence } : {}),
    };
  });

  return merged;
};

function MatchFlowModal({
  isOpen,
  source,
  onClose,
  onAccept,
  acceptButtonLabel = 'Accept Match',
}: MatchFlowModalProps) {
  const [activeStep, setActiveStep] = useState<StepKey>('image');
  const [stages, setStages] = useState(EMPTY_STAGES);
  const [workingImage, setWorkingImage] = useState('');
  const [workingImageType, setWorkingImageType] = useState<
    'original' | 'manual' | 'preprocessed'
  >('original');
  const [selectedCandidates, setSelectedCandidates] = useState<
    Partial<Record<StepKey, number>>
  >({});
  const [selectedResearchCandidate, setSelectedResearchCandidate] = useState<
    number | null
  >(null);
  const [accepted, setAccepted] = useState<MatchFlowAcceptance | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [identifySelection, setIdentifySelection] = useState(
    EMPTY_IDENTIFY_SELECTION
  );
  const [researchMode, setResearchMode] =
    useState<IdentifyResearchMode>('auto');
  const [researchProfile, setResearchProfile] =
    useState<IdentifyResearchProfile>('quick');
  const [hypothesisImports, setHypothesisImports] = useState<
    Record<number, HypothesisImportState>
  >({});
  const [enrichmentPreview, setEnrichmentPreview] =
    useState<EnrichmentPreview | null>(null);
  const [savingEnrichment, setSavingEnrichment] = useState(false);
  const [reimportingEnrichment, setReimportingEnrichment] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState('');
  const [wrapperAnalyses, setWrapperAnalyses] = useState<
    WrapperAnalysisState[]
  >([]);
  const sessionSource = useRef(source);
  const activeStepButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen || !source) return;
    sessionSource.current = source;
    setActiveStep('image');
    setStages(EMPTY_STAGES());
    setWorkingImage(source.image);
    setWorkingImageType(
      source.sourceType === 'upload' && source.originalImage !== source.image
        ? 'manual'
        : 'original'
    );
    setSelectedCandidates({});
    setSelectedResearchCandidate(null);
    setIdentifySelection(EMPTY_IDENTIFY_SELECTION());
    setResearchMode('auto');
    setResearchProfile('quick');
    setHypothesisImports({});
    setEnrichmentPreview(null);
    setSavingEnrichment(false);
    setReimportingEnrichment(false);
    setEnrichmentError('');
    setWrapperAnalyses([]);
    setAccepted(null);
    setIsAccepting(false);
    setAcceptError('');
  }, [isOpen, source]);

  useEffect(() => {
    if (
      !isOpen ||
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia('(max-width: 767px)').matches
    ) {
      return;
    }
    activeStepButton.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeStep, isOpen]);

  const sourceImage = source?.originalImage || source?.image || '';
  const stage = stages[activeStep];
  const activeIndex = STEPS.findIndex(({ key }) => key === activeStep);

  const clearDownstream = (step: StepKey) => {
    const start = STEPS.findIndex(({ key }) => key === step) + 1;
    setStages((current) => {
      const next = { ...current };
      STEPS.slice(start).forEach(({ key }) => {
        next[key] = { status: 'idle' };
      });
      return next;
    });
    setAccepted(null);
  };

  const selectWorkingImage = (
    image: string,
    type: 'original' | 'manual' | 'preprocessed'
  ) => {
    if (!image || image === workingImage) return;
    setWorkingImage(image);
    setWorkingImageType(type);
    clearDownstream('preprocess');
  };

  const runStage = async (key: StepKey) => {
    if (!workingImage || stages[key].status === 'processing') return;
    if (key === 'identify') {
      setSelectedResearchCandidate(null);
      setSelectedCandidates((current) => {
        const next = { ...current };
        delete next.identify;
        return next;
      });
    }
    setActiveStep(key);
    setStages((current) => ({
      ...current,
      [key]: { ...current[key], status: 'processing', error: undefined },
    }));

    try {
      let result: any;
      if (key === 'preprocess') {
        result = await preprocessCigarImage(workingImage);
        if (result.image) {
          try {
            result.publicImageUrl = await uploadPreprocessedImage(result.image);
          } catch (uploadError) {
            result.uploadError = errorMessage(uploadError);
          }
        }
      }
      if (key === 'similar') {
        result = await findSimilarCigarsDebug(workingImage);
        result.hydratedCandidates = await hydrateSimilarityCandidates(
          result.candidates || []
        );
      }
      if (key === 'text') {
        result = await recognizeCigarText(workingImage);
        const ranked = [
          ...(result.proposedMatch
            ? [{ ...result.proposedMatch, proposed: true }]
            : []),
          ...textCandidates(result),
        ].filter(
          (candidate, index, all) =>
            all.findIndex(
              (item) =>
                (item.cigarId || item.cigar_id) ===
                (candidate.cigarId || candidate.cigar_id)
            ) === index
        );
        result.hydratedCandidates = await hydrateCigarCandidates(
          ranked.map((candidate) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }
      if (key === 'fingerprint')
        result = await fingerprintCigarImage(workingImage);
      if (key === 'fingerprintRecognition') {
        const fingerprint = stages.fingerprint.result?.fingerprint;
        if (!fingerprint) throw new Error('Generate a fingerprint first.');
        result = await recognizeCigarFingerprint(fingerprint);
        result.hydratedCandidates = await hydrateCigarCandidates(
          (result.candidates || []).map((candidate: any) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }
      if (key === 'identify') {
        const stageResults: Record<string, unknown> = {};
        if (identifySelection.similar && stages.similar.result) {
          stageResults.similar = rawStageResult(stages.similar.result);
        }
        if (identifySelection.text && stages.text.result) {
          stageResults.text = rawStageResult(stages.text.result);
        }
        if (identifySelection.fingerprint && stages.fingerprint.result) {
          stageResults.fingerprint = rawStageResult(stages.fingerprint.result);
        }
        if (
          identifySelection.recognize &&
          stages.fingerprintRecognition.result
        ) {
          stageResults.recognize = rawStageResult(
            stages.fingerprintRecognition.result
          );
        }
        const relevantWrapperSteps = new Set<WrapperAnalysisSourceStep>([
          ...(identifySelection.similar
            ? (['similar'] as WrapperAnalysisSourceStep[])
            : []),
          ...(identifySelection.text
            ? (['text'] as WrapperAnalysisSourceStep[])
            : []),
          ...(identifySelection.recognize
            ? (['fingerprintRecognize'] as WrapperAnalysisSourceStep[])
            : []),
        ]);
        const selectedWrapperAnalysis = [...wrapperAnalyses]
          .filter(
            (analysis) =>
              analysis.status === 'complete' &&
              analysis.imageUrl === workingImage &&
              relevantWrapperSteps.has(analysis.sourceStep)
          )
          .sort((left, right) => right.updatedAt - left.updatedAt)[0];
        const wrapperAnalysis =
          selectedWrapperAnalysis ||
          [...wrapperAnalyses]
            .filter(
              (analysis) =>
                analysis.status === 'complete' &&
                analysis.imageUrl === workingImage
            )
            .sort((left, right) => right.updatedAt - left.updatedAt)[0];
        if (identifySelection.analyze && wrapperAnalysis?.result) {
          const wrapperResult = wrapperAnalysis.result;
          stageResults.wrapperAnalysis = {
            sourceStep: wrapperAnalysis.sourceStep,
            ambiguity: wrapperResult.ambiguity,
            predictedShade:
              wrapperResult.predictedShade ||
              wrapperResult.predicted ||
              wrapperResult.result?.predicted,
            confidence:
              wrapperResult.confidence || wrapperResult.result?.confidence,
            evidence: wrapperResult.evidence,
            candidateAdjustments: wrapperResult.candidateAdjustments,
            inconclusive: wrapperResult.inconclusive,
            conclusion: wrapperResult.conclusion,
          };
        }
        result = await identifyCigarDebug(
          workingImage,
          stageResults,
          researchMode,
          researchProfile
        );
        const candidates = identificationCandidates(result).map(
          (candidate: any) => ({
            ...candidate,
            cigar_id: candidate.cigar_id ?? candidate.cigarId,
          })
        );
        result.hydratedCandidates = await hydrateCigarCandidates(
          candidates,
          candidates.length
        );
        result.hydratedResearchCandidates = await hydrateCigarCandidates(
          (result.research?.catalogCandidates || []).map((candidate: any) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }

      setStages((current) => ({
        ...current,
        [key]: { status: 'complete', result },
      }));
      if (key === 'preprocess' && workingImageType === 'preprocessed') {
        if (result.publicImageUrl) setWorkingImage(result.publicImageUrl);
        else {
          setWorkingImage(source?.image || sourceImage);
          setWorkingImageType(
            source?.image !== sourceImage ? 'manual' : 'original'
          );
        }
      }
    } catch (error) {
      setStages((current) => ({
        ...current,
        [key]: {
          ...current[key],
          status: 'failed',
          error: errorMessage(error),
        },
      }));
    }
  };

  const skipPreprocess = () => {
    setStages((current) => ({
      ...current,
      preprocess: { ...current.preprocess, status: 'skipped' },
    }));
    setActiveStep('similar');
  };

  const candidates = useMemo(() => {
    const { result } = stage;
    if (!result) return [];
    if (result.hydratedCandidates?.length) return result.hydratedCandidates;
    if (activeStep === 'text') {
      const recognizedCandidates = textCandidates(result);
      if (recognizedCandidates.length) return recognizedCandidates;
      return (
        result.cigarCandidates ||
        result.cigar_candidates ||
        result.candidates ||
        []
      );
    }
    return result.candidates || [];
  }, [activeStep, stage.result]);

  const researchCandidates = useMemo(() => {
    if (activeStep !== 'identify') return [];
    return stage.result?.hydratedResearchCandidates?.length
      ? stage.result.hydratedResearchCandidates
      : stage.result?.research?.catalogCandidates || [];
  }, [activeStep, stage.result]);

  const runUsage = useMemo(
    () =>
      STEPS.reduce(
        (total, { key }) => {
          const { result } = stages[key];
          const usage = tokenUsage(result);
          return {
            tokens:
              total.tokens +
              Number(usage?.totalTokens ?? usage?.total_tokens ?? 0),
            cost: total.cost + estimatedCost(result),
          };
        },
        { tokens: 0, cost: 0 }
      ),
    [stages]
  );

  const selectedCandidate =
    activeStep === 'identify' && selectedResearchCandidate !== null
      ? researchCandidates[selectedResearchCandidate]
      : candidates[selectedCandidates[activeStep] ?? 0];
  const acceptCandidate = async (
    candidate = selectedCandidate,
    step: StepKey = activeStep,
    confidence = candidateScore(candidate)
  ) => {
    if (!candidate || isAccepting) return;
    const cigarId = candidateCigarId(candidate);
    if (cigarId === undefined || cigarId === null || cigarId === '') {
      setAcceptError('The selected candidate does not have a cigar ID.');
      return;
    }
    const acceptance: MatchFlowAcceptance = {
      candidate,
      cigarId,
      step,
      confidence,
    };
    setIsAccepting(true);
    setAcceptError('');
    try {
      await onAccept?.(acceptance);
      setAccepted(acceptance);
    } catch (error) {
      setAcceptError(errorMessage(error));
    } finally {
      setIsAccepting(false);
    }
  };

  const nextStep = () => {
    const next = STEPS[activeIndex + 1];
    if (!next) return;
    setActiveStep(next.key);
    if (stages[next.key].status === 'idle') runStage(next.key);
  };

  const renderEvidence = (
    content: React.ReactNode,
    label = 'View step evidence'
  ) => (
    <details className="match-flow-evidence-collapse">
      <summary>{label}</summary>
      <div className="match-flow-evidence-content">{content}</div>
    </details>
  );

  const renderCandidateEvidence = () => {
    const evidencedCandidates = candidates.filter(
      (candidate: any) => values(candidate.evidence).length > 0
    );
    if (!evidencedCandidates.length) return null;
    return (
      <div className="match-flow-mobile-candidate-evidence">
        <strong>Candidate evidence</strong>
        {evidencedCandidates.map((candidate: any, index: number) => (
          <div key={`${candidateCigarId(candidate) ?? index}-evidence`}>
            <b>{candidateLabel(candidate)}</b>
            <span>{values(candidate.evidence).join(' · ')}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMeta = (result: any, fallbackCost: string) => (
    <div className="match-flow-meta">
      <span>
        {result?.meta?.paidInference ? 'Paid inference' : fallbackCost}
      </span>
      {duration(result) && <span>{duration(result)}</span>}
      {(result?.meta?.provider || result?.metadata?.source) && (
        <span>{result.meta?.provider || result.metadata?.source}</span>
      )}
      {(result?.meta?.model || result?.metadata?.multimodal_model) && (
        <span>{result.meta?.model || result.metadata?.multimodal_model}</span>
      )}
      {tokenUsage(result) && (
        <span>
          {Number(
            tokenUsage(result).totalTokens ??
              tokenUsage(result).total_tokens ??
              0
          ).toLocaleString()}{' '}
          tokens ·{' '}
          {Number(
            tokenUsage(result).inputTokens ??
              tokenUsage(result).input_tokens ??
              0
          ).toLocaleString()}{' '}
          in /{' '}
          {Number(
            tokenUsage(result).outputTokens ??
              tokenUsage(result).output_tokens ??
              0
          ).toLocaleString()}{' '}
          out
        </span>
      )}
      {estimatedCost(result) > 0 && (
        <span>{formatCost(estimatedCost(result))} estimated</span>
      )}
      {(result?.meta?.costBasis || result?.meta?.cost_basis) && (
        <span>
          {(result.meta.costBasis || result.meta.cost_basis)
            .split('_')
            .join(' ')}
        </span>
      )}
    </div>
  );

  const currentWrapperSourceStep = (): WrapperAnalysisSourceStep | null => {
    if (activeStep === 'similar' || activeStep === 'text') return activeStep;
    if (activeStep === 'fingerprintRecognition') {
      return 'fingerprintRecognize';
    }
    if (activeStep === 'identify') return 'identify';
    return null;
  };

  const currentRawCandidates = () => {
    const { result } = stage;
    if (!result) return [];
    if (activeStep === 'text') {
      return [
        ...(result.proposedMatch ? [result.proposedMatch] : []),
        ...textCandidates(result),
      ];
    }
    if (activeStep === 'identify') {
      return identificationCandidates(result);
    }
    return result.candidates || [];
  };

  const runWrapperAnalysis = async (
    allCandidates: any[],
    tiedCandidates: any[],
    sourceStep: WrapperAnalysisSourceStep
  ) => {
    const key = wrapperAnalysisKey(tiedCandidates, workingImage);
    const current = wrapperAnalyses.find((analysis) => analysis.key === key);
    if (current?.status === 'processing') return;
    const candidateIds = tiedCandidates.map(wrapperCandidateId).sort();
    const pending: WrapperAnalysisState = {
      key,
      imageUrl: workingImage,
      sourceStep,
      candidateIds,
      status: 'processing',
      updatedAt: Date.now(),
    };
    setWrapperAnalyses((analyses) => [
      ...analyses.filter((analysis) => analysis.key !== key),
      pending,
    ]);
    try {
      const result = await analyzeCigarWrapper({
        imageUrl: workingImage,
        candidates: allCandidates,
      });
      setWrapperAnalyses((analyses) => [
        ...analyses.filter((analysis) => analysis.key !== key),
        {
          ...pending,
          sourceStep,
          result,
          status: 'complete',
          updatedAt: Date.now(),
        },
      ]);
    } catch (error) {
      setWrapperAnalyses((analyses) => [
        ...analyses.filter((analysis) => analysis.key !== key),
        {
          ...pending,
          status: 'failed',
          error: errorMessage(error),
          updatedAt: Date.now(),
        },
      ]);
    }
  };

  const renderWrapperAnalysis = () => {
    const sourceStep = currentWrapperSourceStep();
    const allCandidates = currentRawCandidates();
    const tiedCandidates = getTiedCandidates(allCandidates);
    if (!sourceStep || tiedCandidates.length < 2) return null;
    const key = wrapperAnalysisKey(tiedCandidates, workingImage);
    const analysis = wrapperAnalyses.find((item) => item.key === key);
    const result = analysis?.result;
    const predicted =
      result?.predictedShade ||
      result?.predicted ||
      result?.favored ||
      result?.result?.predicted;
    const wrapperConfidence = result?.confidence || result?.result?.confidence;
    const ambiguity =
      result?.ambiguity?.values ||
      result?.ambiguity?.label ||
      result?.ambiguity;
    const evidenceEntries = Object.entries(result?.evidence || {});
    const adjustments =
      result?.candidateAdjustments || result?.candidate_adjustments || [];
    const inconclusive = Boolean(
      result?.skipped ||
        result?.analyzed === false ||
        result?.inconclusive ||
        result?.conclusive === false ||
        result?.status === 'inconclusive'
    );
    let analysisButtonLabel = 'Analyze Wrapper';
    if (analysis?.status === 'processing') {
      analysisButtonLabel = 'Analyzing Wrapper…';
    } else if (analysis?.status === 'complete') {
      analysisButtonLabel = 'Run Again';
    }
    let resultLabel = 'No favored wrapper returned';
    if (inconclusive) {
      resultLabel = 'Inconclusive';
    } else if (predicted) {
      const confidenceLabel = percentage(wrapperConfidence);
      resultLabel = `${predicted} favored${confidenceLabel ? ` — ${confidenceLabel}` : ''}`;
    }

    return (
      <section className="match-flow-wrapper-analysis">
        <div className="match-flow-wrapper-analysis-heading">
          <div>
            <strong>Wrapper Analysis</strong>
            {analysis?.status === 'complete' && (
              <span>
                Wrapper analyzed
                {predicted ? ` — ${predicted} favored` : ''}
                {analysis.sourceStep !== sourceStep ? ' · reused' : ''}
              </span>
            )}
          </div>
          <Button
            size="sm"
            color="secondary"
            disabled={analysis?.status === 'processing'}
            onClick={() =>
              runWrapperAnalysis(allCandidates, tiedCandidates, sourceStep)
            }
          >
            {analysisButtonLabel}
          </Button>
        </div>
        {analysis?.status === 'failed' && (
          <div className="match-flow-wrapper-error">{analysis.error}</div>
        )}
        {analysis?.status === 'complete' && (
          <>
            {renderEvidence(
              <div className="match-flow-wrapper-result">
                {ambiguity && (
                  <div>
                    <small>Ambiguity</small>
                    <strong>{values(ambiguity).join(' vs ')}</strong>
                  </div>
                )}
                <div>
                  <small>Result</small>
                  <strong>{resultLabel}</strong>
                  {(result.explanation ||
                    result.reasoning ||
                    result.message) && (
                    <p>
                      {result.explanation || result.reasoning || result.message}
                    </p>
                  )}
                </div>
                {evidenceEntries.length > 0 && (
                  <div>
                    <small>Evidence</small>
                    <ul>
                      {evidenceEntries.map(([label, value]) => (
                        <li key={label}>
                          {label
                            .replace(/([a-z])([A-Z])/g, '$1 $2')
                            .split('_')
                            .join(' ')}
                          : {formatEvidenceValue(value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {adjustments.length > 0 && (
                  <div>
                    <small>Candidate adjustments</small>
                    <ul>
                      {adjustments.map((adjustment: any, index: number) => (
                        <li key={adjustment.candidateId || index}>
                          {adjustment.label ||
                            adjustment.name ||
                            (adjustment.wrapperShade
                              ? `${adjustment.wrapperShade} · cigar ${adjustment.cigarId}`
                              : null) ||
                            adjustment.candidateId ||
                            'Candidate'}{' '}
                          {Number(
                            adjustment.scoreAdjustment ?? adjustment.adjustment
                          ) >= 0
                            ? '+'
                            : ''}
                          {adjustment.scoreAdjustment ?? adjustment.adjustment}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <details>
                  <summary>Raw wrapper response</summary>
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </details>
              </div>,
              'View wrapper evidence'
            )}
          </>
        )}
      </section>
    );
  };

  const renderCandidates = () => (
    <>
      <div className="match-flow-candidates">
        {!candidates.length && <p>No candidate identities were returned.</p>}
        {candidates.map((candidate: any, index: number) => (
          <button
            type="button"
            className={`match-flow-candidate${
              (selectedCandidates[activeStep] ?? 0) === index ? ' selected' : ''
            }`}
            key={`${candidate.cigar_id || candidate.cigarId || index}-${index}`}
            onClick={() => {
              if (activeStep === 'identify') setSelectedResearchCandidate(null);
              setSelectedCandidates((current) => ({
                ...current,
                [activeStep]: index,
              }));
            }}
          >
            {candidateImage(candidate) && (
              <img src={candidateImage(candidate)} alt="" />
            )}
            <span>
              <strong>{candidateLabel(candidate)}</strong>
              <small>
                {candidate.proposed && 'Proposed match · '}
                {candidate.resultType === 'consensus' &&
                  'Consensus candidate · '}
                {candidateScore(candidate) || 'Score unavailable'}
                {(candidate.brand || candidate.cigar?.brand?.name) &&
                  ` · ${candidate.brand || candidate.cigar.brand.name}`}
              </small>
              {values(candidate.evidence).length > 0 && (
                <em className="match-flow-candidate-inline-evidence">
                  {values(candidate.evidence).join(' · ')}
                </em>
              )}
            </span>
          </button>
        ))}
      </div>
      {renderWrapperAnalysis()}
    </>
  );

  const renderFingerprint = (result: any) => {
    const fingerprint = result?.fingerprint || {};
    const renderPalette = (palette: any[] = []) => (
      <div className="match-flow-fingerprint-palette">
        {palette.map((color, index) => (
          <span key={`${color.color || color.name}-${index}`}>
            <i style={{ backgroundColor: color.color || color.name }} />
            <b>{color.color || color.name || 'Unknown'}</b>
            {percentage(color.confidence ?? color.prominence) && (
              <small>{percentage(color.confidence ?? color.prominence)}</small>
            )}
            {color.finish && <small>{color.finish}</small>}
            {color.location && <small>{color.location}</small>}
          </span>
        ))}
      </div>
    );
    const fields = [
      ['Colors', fingerprint.colors],
      ['Symbols', fingerprint.symbols],
      ['Motifs', fingerprint.motifs],
      ['Objects', fingerprint.objects],
      ['Text style', fingerprint.textStyle],
      ['Monograms', fingerprint.monograms],
      ['Shape', fingerprint.shape],
    ];
    return (
      <div className="match-flow-fingerprint">
        {(fingerprint.schemaVersion || percentage(fingerprint.confidence)) && (
          <section>
            <strong>Fingerprint</strong>
            <p>
              {fingerprint.schemaVersion || 'Legacy schema'}
              {percentage(fingerprint.confidence)
                ? ` · ${percentage(fingerprint.confidence)} confidence`
                : ''}
            </p>
          </section>
        )}
        {fields.map(([label, value]) =>
          values(value).length ? (
            <section key={String(label)}>
              <strong>{label}</strong>
              <div>
                {values(value).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>
          ) : null
        )}
        {fingerprint.layout && (
          <section>
            <strong>Layout</strong>
            <p>
              {Object.values(fingerprint.layout).filter(Boolean).join(' · ')}
            </p>
          </section>
        )}
        {fingerprint.bandAppearance && (
          <section className="match-flow-fingerprint-wide">
            <strong>Band appearance</strong>
            {[
              ['Background', fingerprint.bandAppearance.backgroundColors],
              ['Border', fingerprint.bandAppearance.borderColors],
              ['Accents', fingerprint.bandAppearance.accentColors],
              ['Dominant palette', fingerprint.bandAppearance.dominantPalette],
            ].map(([label, palette]: any) =>
              palette?.length ? (
                <div className="match-flow-fingerprint-detail" key={label}>
                  <small>{label}</small>
                  {renderPalette(palette)}
                </div>
              ) : null
            )}
          </section>
        )}
        {fingerprint.typography?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Typography</strong>
            <div className="match-flow-fingerprint-typography">
              {fingerprint.typography.map((type: any, index: number) => (
                <div key={`${type.text || type.role}-${index}`}>
                  <b>{type.text || 'Unrecognized text'}</b>
                  <span>{type.role || 'unspecified role'}</span>
                  {type.color && type.color !== 'null' && (
                    <span>{type.color}</span>
                  )}
                  {type.location && <span>{type.location}</span>}
                  {percentage(type.confidence) && (
                    <small>{percentage(type.confidence)}</small>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {fingerprint.colorRelationships?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Color relationships</strong>
            <div className="match-flow-fingerprint-relationships">
              {fingerprint.colorRelationships.map(
                (relationship: any, index: number) => (
                  <span key={`${relationship.role}-${index}`}>
                    <b>
                      {relationship.foreground || 'Foreground'} on{' '}
                      {relationship.background || 'background'}
                    </b>
                    <small>
                      {[
                        relationship.role,
                        relationship.contrast,
                        percentage(relationship.confidence),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </span>
                )
              )}
            </div>
          </section>
        )}
        {fingerprint.bands?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Detected bands</strong>
            <div className="match-flow-detected-bands">
              {fingerprint.bands.map((band: any, index: number) => (
                <div key={`${band.role}-${index}`}>
                  <b>{band.role || `Band ${index + 1}`}</b>
                  {band.appearance?.dominantPalette?.length > 0 &&
                    renderPalette(band.appearance.dominantPalette)}
                  {band.typography?.length > 0 && (
                    <small>
                      Text:{' '}
                      {band.typography
                        .map((type: any) => type.text)
                        .filter(Boolean)
                        .join(', ')}
                    </small>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {(fingerprint.description || fingerprint.artwork) && (
          <section className="match-flow-fingerprint-wide">
            <strong>Description</strong>
            <p>{fingerprint.description || fingerprint.artwork}</p>
          </section>
        )}
      </div>
    );
  };

  const renderText = (result: any) => (
    <div className="match-flow-text-result">
      <section>
        <strong>Detected text</strong>
        <pre>{result.rawText || 'No text detected'}</pre>
      </section>
      {result.normalizedText && (
        <section>
          <strong>Normalized</strong>
          <p>{result.normalizedText}</p>
        </section>
      )}
      {[
        ['Tokens', result.tokens],
        ['Brands', result.brandCandidates || result.brand_candidates],
        ['Names', result.nameCandidates || result.name_candidates],
        ['Series', result.seriesCandidates || result.series_candidates],
        ['Aliases', result.aliases],
        ['Tags', result.tags || result.matchingTags],
        ['Barcodes', result.barcodeCandidates || result.barcode_candidates],
        ['Other clues', result.otherClues || result.other_clues],
      ].map(([label, value]) =>
        values(value).length ? (
          <section key={String(label)}>
            <strong>{label}</strong>
            <div className="match-flow-chips">
              {values(value).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </section>
        ) : null
      )}
      {percentage(result.confidence) && (
        <p>Confidence {percentage(result.confidence)}</p>
      )}
    </div>
  );

  const importHypothesis = async (
    hypothesis: any,
    index: number,
    research: any
  ) => {
    if (hypothesisImports[index]?.status === 'importing') return;
    setHypothesisImports((current) => ({
      ...current,
      [index]: { status: 'importing' },
    }));
    try {
      const sourceUrl =
        hypothesis.sourceUrls?.[0] || research.sources?.[0]?.url;
      const enrichment = await enrichCigarHypothesis({
        ...(sourceUrl ? { sourceUrl } : {}),
        cigar: {
          brand: hypothesis.brand || null,
          name: hypothesis.name || null,
          line: hypothesis.line || null,
          aliases: hypothesis.aliases || [],
          countryOfOrigin: hypothesis.countryOfOrigin || null,
          wrapper: hypothesis.wrapper || null,
          binder: hypothesis.binder || null,
          filler: hypothesis.filler || null,
          strength: hypothesis.strength || null,
        },
      });
      const enriched = enrichment.cigar || enrichment;
      const editableValue = (value: unknown) =>
        Array.isArray(value) ? value.join(', ') : value || '';
      const brand = enriched.brand || hypothesis.brand || '';
      setEnrichmentPreview({
        index,
        hypothesis,
        sourceUrl: sourceUrl || '',
        brand: brand ? { value: brand, label: brand } : null,
        data: {
          ...enriched,
          brand,
          name: enriched.name || hypothesis.name || '',
          line: enriched.line || hypothesis.line || '',
          aliases: editableValue(enriched.aliases || hypothesis.aliases),
          countryOfOrigin: editableValue(
            enriched.countryOfOrigin ||
              enriched.origin ||
              hypothesis.countryOfOrigin
          ),
          wrapper: editableValue(enriched.wrapper),
          binder: editableValue(enriched.binder),
          filler: editableValue(enriched.filler),
          strength: editableValue(enriched.strength),
        },
      });
      setEnrichmentError('');
    } catch (error) {
      setHypothesisImports((current) => ({
        ...current,
        [index]: { status: 'failed', error: errorMessage(error) },
      }));
    }
  };

  const closeEnrichmentPreview = () => {
    if (savingEnrichment || reimportingEnrichment) return;
    if (enrichmentPreview) {
      setHypothesisImports((current) => {
        const next = { ...current };
        delete next[enrichmentPreview.index];
        return next;
      });
    }
    setEnrichmentPreview(null);
    setEnrichmentError('');
  };

  const reimportEnrichment = async () => {
    if (!enrichmentPreview || reimportingEnrichment) return;
    const { brand, data, sourceUrl } = enrichmentPreview;
    if (!sourceUrl.trim()) {
      setEnrichmentError('Enter a source URL before re-importing.');
      return;
    }
    setReimportingEnrichment(true);
    setEnrichmentError('');
    try {
      const enrichment = await enrichCigarHypothesis({
        sourceUrl: sourceUrl.trim(),
        cigar: {
          brand: brand?.label || data.brand || null,
          name: data.name || null,
          line: data.line || null,
          aliases: String(data.aliases || '')
            .split(',')
            .map((alias) => alias.trim())
            .filter(Boolean),
          countryOfOrigin: data.countryOfOrigin || null,
          wrapper: data.wrapper || null,
          binder: data.binder || null,
          filler: data.filler || null,
          strength: data.strength || null,
        },
      });
      const refreshed = enrichment.cigar || enrichment;
      const editableValue = (value: unknown) =>
        Array.isArray(value) ? value.join(', ') : value || '';
      const refreshedBrand = refreshed.brand || brand?.label || '';
      let nextBrand: BrandOption | null = null;
      if (brand?.label === refreshedBrand) {
        nextBrand = brand;
      } else if (refreshedBrand) {
        nextBrand = { value: refreshedBrand, label: refreshedBrand };
      }
      setEnrichmentPreview((current) =>
        current
          ? {
              ...current,
              sourceUrl: sourceUrl.trim(),
              brand: nextBrand,
              data: {
                ...current.data,
                ...refreshed,
                brand: refreshedBrand,
                name: refreshed.name || current.data.name || '',
                line: refreshed.line || '',
                aliases: editableValue(refreshed.aliases),
                countryOfOrigin: editableValue(
                  refreshed.countryOfOrigin || refreshed.origin
                ),
                wrapper: editableValue(refreshed.wrapper),
                binder: editableValue(refreshed.binder),
                filler: editableValue(refreshed.filler),
                strength: editableValue(refreshed.strength),
              },
            }
          : current
      );
    } catch (error) {
      setEnrichmentError(errorMessage(error));
    } finally {
      setReimportingEnrichment(false);
    }
  };

  const loadBrandOptions = async (query: string): Promise<BrandOption[]> => {
    const brands: any[] = await getBrands(query);
    return brands.map((brand) => ({
      value: brand.id,
      label: brand.name,
    }));
  };

  const saveEnrichedCigar = async () => {
    if (!enrichmentPreview || savingEnrichment) return;
    const { brand, data, index } = enrichmentPreview;
    if (!brand?.label || !String(data.name || '').trim()) {
      setEnrichmentError('Brand and name are required.');
      return;
    }
    setSavingEnrichment(true);
    setEnrichmentError('');
    try {
      const brandId = brand.__isNew__ ? NaN : Number(brand.value);
      const origin = String(data.countryOfOrigin || '').trim() || null;
      const createdCigar = await postCigar({
        ...data,
        brand: brand.label,
        brand_id: Number.isInteger(brandId) ? brandId : undefined,
        name: String(data.name).trim(),
        line: String(data.line || '').trim() || null,
        aliases: String(data.aliases || '')
          .split(',')
          .map((alias) => alias.trim())
          .filter(Boolean),
        origin,
        countryOfOrigin: undefined,
        wrapper: String(data.wrapper || '').trim() || null,
        binder: String(data.binder || '').trim() || null,
        filler: String(data.filler || '').trim() || null,
        strength: String(data.strength || '').trim() || null,
        pending: false,
        custom: false,
        cuban: origin === 'Cuba',
      });
      if (!createdCigar?.id) {
        throw new Error('The admin server did not return a cigar ID.');
      }
      setHypothesisImports((current) => ({
        ...current,
        [index]: { status: 'complete', cigar: createdCigar },
      }));
      setEnrichmentPreview(null);
    } catch (error) {
      setEnrichmentError(errorMessage(error));
    } finally {
      setSavingEnrichment(false);
    }
  };

  const renderResearch = (result: any) => {
    const research = result?.research;
    if (!research?.attempted) return null;
    const hypotheses = research.hypotheses || [];
    const catalogCandidates = result.hydratedResearchCandidates?.length
      ? result.hydratedResearchCandidates
      : research.catalogCandidates || [];

    return (
      <div className="match-flow-research">
        <div className="match-flow-section-heading">
          <div>
            <h6>Research findings</h6>
            <span>
              {research.mode || researchMode}
              {` · ${research.profile || research.researchProfile || researchProfile}`}
              {research.model ? ` · ${research.model}` : ''}
              {research.decisionReason
                ? ` · ${research.decisionReason.split('_').join(' ')}`
                : ''}
            </span>
          </div>
        </div>
        {research.summary &&
          renderEvidence(
            <p className="match-flow-research-summary">{research.summary}</p>,
            'View research summary'
          )}
        {hypotheses.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Potential new catalog entries</h6>
                <span>
                  Research hypotheses that are not linked to a database cigar
                </span>
              </div>
            </div>
            <div className="match-flow-hypotheses">
              {hypotheses.map((hypothesis: any, index: number) => (
                <article
                  key={`${hypothesis.brand}-${hypothesis.name}-${index}`}
                >
                  <header>
                    <div>
                      <strong>
                        {[hypothesis.brand, hypothesis.name]
                          .filter(Boolean)
                          .join(' · ') || 'Unidentified cigar'}
                      </strong>
                      {hypothesis.line && <small>{hypothesis.line}</small>}
                    </div>
                    {percentage(hypothesis.confidence) && (
                      <b>{percentage(hypothesis.confidence)}</b>
                    )}
                  </header>
                  {(hypothesis.reasoning ||
                    values(hypothesis.evidence).length > 0 ||
                    values(hypothesis.visualTags).length > 0) &&
                    renderEvidence(
                      <>
                        {hypothesis.reasoning && <p>{hypothesis.reasoning}</p>}
                        {values(hypothesis.evidence).length > 0 && (
                          <ul>
                            {values(hypothesis.evidence).map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {values(hypothesis.visualTags).length > 0 && (
                          <div className="match-flow-chips">
                            {values(hypothesis.visualTags).map((tag) => (
                              <span key={tag}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </>,
                      'View hypothesis evidence'
                    )}
                  <div className="match-flow-hypothesis-actions">
                    {hypothesisImports[index]?.status === 'complete' ? (
                      <>
                        <span>
                          Added as cigar #{hypothesisImports[index].cigar.id}
                        </span>
                        <Button
                          size="sm"
                          color="success"
                          disabled={isAccepting}
                          onClick={() =>
                            acceptCandidate(
                              hypothesisImports[index].cigar,
                              'identify',
                              percentage(hypothesis.confidence)
                            )
                          }
                        >
                          {isAccepting ? (
                            <>
                              <Spinner size="sm" /> Matching…
                            </>
                          ) : (
                            acceptButtonLabel
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        color="primary"
                        disabled={
                          hypothesisImports[index]?.status === 'importing'
                        }
                        onClick={() =>
                          importHypothesis(hypothesis, index, research)
                        }
                      >
                        {hypothesisImports[index]?.status === 'importing' ? (
                          <>
                            <Spinner size="sm" /> Importing…
                          </>
                        ) : (
                          'Import'
                        )}
                      </Button>
                    )}
                  </div>
                  {hypothesisImports[index]?.status === 'failed' && (
                    <p className="match-flow-hypothesis-error">
                      {hypothesisImports[index].error}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        {catalogCandidates.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Research catalog candidates</h6>
                <span>Existing database cigars found during research</span>
              </div>
            </div>
            <div className="match-flow-research-candidates">
              {catalogCandidates.map((candidate: any, index: number) => (
                <button
                  type="button"
                  className={
                    selectedResearchCandidate === index ? 'selected' : ''
                  }
                  key={`${candidate.cigarId || candidate.cigar_id || index}-${index}`}
                  onClick={() => {
                    setSelectedResearchCandidate(index);
                    setSelectedCandidates((current) => ({
                      ...current,
                      identify: -1,
                    }));
                  }}
                >
                  {candidateBandImage(candidate) && (
                    <img src={candidateBandImage(candidate)} alt="" />
                  )}
                  <span>
                    <strong>{candidateLabel(candidate)}</strong>
                    <small>
                      {candidateScore(candidate) || 'Score unavailable'}
                      {candidate.brand ? ` · ${candidate.brand}` : ''}
                    </small>
                    {values(candidate.matchedBy).length > 0 && (
                      <em className="match-flow-candidate-inline-evidence">
                        Matched by {values(candidate.matchedBy).join(' · ')}
                      </em>
                    )}
                  </span>
                </button>
              ))}
            </div>
            {catalogCandidates.some(
              (candidate: any) => values(candidate.matchedBy).length > 0
            ) &&
              renderEvidence(
                <div className="match-flow-mobile-candidate-evidence">
                  <strong>Research candidate evidence</strong>
                  {catalogCandidates
                    .filter(
                      (candidate: any) => values(candidate.matchedBy).length > 0
                    )
                    .map((candidate: any, index: number) => (
                      <div
                        key={`${candidateCigarId(candidate) ?? index}-research-evidence`}
                      >
                        <b>{candidateLabel(candidate)}</b>
                        <span>
                          Matched by {values(candidate.matchedBy).join(' · ')}
                        </span>
                      </div>
                    ))}
                </div>,
                'View research candidate evidence'
              )}
          </section>
        )}
        {research.sources?.length > 0 && (
          <>
            {renderEvidence(
              <section className="match-flow-research-sources">
                <strong>Sources</strong>
                {research.sources.map((sourceItem: any) => (
                  <a
                    href={sourceItem.url}
                    target="_blank"
                    rel="noreferrer"
                    key={sourceItem.url}
                  >
                    {sourceItem.title || sourceItem.url}
                  </a>
                ))}
              </section>,
              'View research sources'
            )}
          </>
        )}
      </div>
    );
  };

  const renderResult = () => {
    if (stage.status === 'processing') {
      return (
        <div className="match-flow-processing">
          <Spinner />
          <strong>Processing…</strong>
          <span>Completed results remain available from the steps above.</span>
        </div>
      );
    }
    if (stage.status === 'failed') {
      return (
        <div className="match-flow-error">
          <strong>Stage failed</strong>
          <span>{stage.error}</span>
        </div>
      );
    }
    if (stage.status === 'idle') {
      return (
        <div className="match-flow-empty">
          <strong>This stage has not run.</strong>
          <span>Run it when you are ready to inspect its output.</span>
        </div>
      );
    }
    if (stage.status === 'skipped') {
      return (
        <div className="match-flow-empty">
          <strong>Preprocessing skipped</strong>
          <span>Subsequent stages use the selected original/manual image.</span>
        </div>
      );
    }
    if (activeStep === 'image') return null;
    if (activeStep === 'preprocess') {
      const { result } = stage;
      return (
        <>
          {renderEvidence(
            <>
              <div className="match-flow-preprocess-output">
                {result?.image ? (
                  <img
                    src={result.image}
                    alt="Automatically isolated cigar band"
                  />
                ) : (
                  <div>No safe crop was produced.</div>
                )}
                <div>
                  <strong>
                    Confidence {percentage(result?.confidence) || '—'}
                  </strong>
                  {result?.region && (
                    <p>
                      Region: {result.region.x}, {result.region.y} ·{' '}
                      {result.region.width} × {result.region.height}
                    </p>
                  )}
                  <p>{values(result?.transformations).join(' · ')}</p>
                  {values(result?.notes).map((note) => (
                    <p key={note}>{note}</p>
                  ))}
                  {result?.uploadError && (
                    <p className="text-danger">
                      Processed-image upload failed: {result.uploadError}
                    </p>
                  )}
                </div>
              </div>
              {renderMeta(result, 'Free · Local')}
            </>,
            'View isolation evidence'
          )}{' '}
          {result?.publicImageUrl && (
            <Button
              color="primary"
              onClick={() =>
                selectWorkingImage(result.publicImageUrl, 'preprocessed')
              }
            >
              Use processed image
            </Button>
          )}
        </>
      );
    }
    if (activeStep === 'text')
      return (
        <>
          <div className="match-flow-section-heading">
            <div>
              <h6>Text catalog candidates</h6>
              <span>
                Existing database cigars matched from the recognized text
              </span>
            </div>
          </div>
          {renderCandidates()}
          {renderEvidence(
            <>
              {renderText(stage.result)}
              {renderCandidateEvidence()}
              {renderMeta(stage.result, 'Paid · Vision')}
            </>,
            'View text evidence'
          )}
        </>
      );
    if (activeStep === 'fingerprint')
      return renderEvidence(
        <>
          {renderFingerprint(stage.result)}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>,
        'View fingerprint evidence'
      );
    if (activeStep === 'identify')
      return (
        <>
          <div className="match-flow-section-heading">
            <div>
              <h6>Match and suggestions</h6>
              <span>
                Selectable match, suggestion, and consensus results linked to
                existing database cigars
              </span>
            </div>
          </div>
          {renderCandidates()}
          {renderResearch(stage.result)}
          {renderEvidence(
            <>
              {renderCandidateEvidence()}
              {renderMeta(stage.result, STEPS[activeIndex].cost)}
            </>
          )}
        </>
      );
    return (
      <>
        {renderCandidates()}
        {renderEvidence(
          <>
            {renderCandidateEvidence()}
            {renderMeta(stage.result, STEPS[activeIndex].cost)}
          </>
        )}
      </>
    );
  };

  const identifyOptions: Array<{
    key: IdentifyStageKey;
    label: string;
    available: boolean;
  }> = [
    {
      key: 'similar',
      label: 'Similar',
      available: Boolean(stages.similar.result),
    },
    {
      key: 'text',
      label: 'Text',
      available: Boolean(stages.text.result),
    },
    {
      key: 'fingerprint',
      label: 'Fingerprint',
      available: Boolean(stages.fingerprint.result),
    },
    {
      key: 'recognize',
      label: 'Recognize',
      available: Boolean(stages.fingerprintRecognition.result),
    },
    {
      key: 'analyze',
      label: 'Analyze',
      available: wrapperAnalyses.some(
        (analysis) =>
          analysis.status === 'complete' && analysis.imageUrl === workingImage
      ),
    },
  ];

  if (!source) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        className="match-flow-dialog"
        fade
      >
        <div className="modal-header match-flow-header">
          <div>
            <h5 className="modal-title">Cigar Match Flow</h5>
            <small>
              {source.sourceType.replace('_', ' ')}
              {source.sourceId !== undefined ? ` · ${source.sourceId}` : ''}
            </small>
          </div>
          <button type="button" className="close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="match-flow-steps">
          {STEPS.map((item, index) => {
            const { status } = stages[item.key];
            return (
              <React.Fragment key={item.key}>
                <button
                  type="button"
                  ref={item.key === activeStep ? activeStepButton : null}
                  className={`match-flow-step ${status}${item.key === activeStep ? ' active' : ''}`}
                  onClick={() => setActiveStep(item.key)}
                >
                  <span>
                    {status === 'processing' ? (
                      <Spinner size="sm" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <strong>{item.label}</strong>
                  <small>{item.cost}</small>
                </button>
                {index < STEPS.length - 1 && <i>→</i>}
              </React.Fragment>
            );
          })}
        </div>
        <div className="modal-body match-flow-body">
          <aside className="match-flow-images">
            <div>
              <span>Original upload</span>
              <img src={sourceImage} alt="Original cigar" />
            </div>
            {source.image !== sourceImage && (
              <div>
                <span>Manual crop</span>
                <img src={source.image} alt="Manually cropped cigar" />
              </div>
            )}
            <div className="match-flow-working">
              <span>Working image · {workingImageType}</span>
              <img src={workingImage} alt="Current recognition input" />
            </div>
            <div className="match-flow-image-buttons">
              <Button
                size="sm"
                disabled={workingImage === sourceImage}
                onClick={() => selectWorkingImage(sourceImage, 'original')}
              >
                Use original
              </Button>
              {source.image !== sourceImage && (
                <Button
                  size="sm"
                  disabled={workingImage === source.image}
                  onClick={() => selectWorkingImage(source.image, 'manual')}
                >
                  Use manual crop
                </Button>
              )}
            </div>
          </aside>
          <main className="match-flow-content">
            <div className="match-flow-stage-heading">
              <div>
                <h5>{STEPS[activeIndex].label}</h5>
                <span>{STEPS[activeIndex].cost}</span>
              </div>
              {activeStep !== 'image' && (
                <Button
                  size="sm"
                  disabled={stage.status === 'processing'}
                  onClick={() => {
                    clearDownstream(activeStep);
                    runStage(activeStep);
                  }}
                >
                  {stage.status === 'idle' ? 'Run Step' : 'Run Again'}
                </Button>
              )}
            </div>
            {activeStep === 'identify' && (
              <div className="match-flow-identify-controls">
                <fieldset className="match-flow-identify-options">
                  <legend>Include previous results</legend>
                  {identifyOptions.map(({ key, label, available }) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={identifySelection[key] && available}
                        disabled={!available || stage.status === 'processing'}
                        onChange={(event) =>
                          setIdentifySelection((current) => ({
                            ...current,
                            [key]: event.target.checked,
                          }))
                        }
                      />
                      <span>{label}</span>
                      {!available && <small>Not run</small>}
                    </label>
                  ))}
                </fieldset>
                <fieldset className="match-flow-identify-options">
                  <legend>Research strategy</legend>
                  {(
                    [
                      'auto',
                      'open_world',
                      'catalog_guided',
                    ] as IdentifyResearchMode[]
                  ).map((mode) => (
                    <label key={mode}>
                      <input
                        type="radio"
                        name="match-flow-research-mode"
                        value={mode}
                        checked={researchMode === mode}
                        disabled={stage.status === 'processing'}
                        onChange={() => setResearchMode(mode)}
                      />
                      <span>{mode.split('_').join(' ')}</span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="match-flow-identify-options">
                  <legend>Research profile</legend>
                  {(['quick', 'thorough'] as IdentifyResearchProfile[]).map(
                    (mode) => (
                      <label key={mode}>
                        <input
                          type="radio"
                          name="match-flow-research-profile"
                          value={mode}
                          checked={researchProfile === mode}
                          disabled={stage.status === 'processing'}
                          onChange={() => setResearchProfile(mode)}
                        />
                        <span>{mode}</span>
                      </label>
                    )
                  )}
                </fieldset>
              </div>
            )}
            {activeStep === 'image' && (
              <div className="match-flow-image-start">
                <h4>Choose how to begin</h4>
                <p>
                  Keep the current image selected, optionally isolate the band,
                  or skip directly to embedding similarity.
                </p>
                <div>
                  <Button
                    color="primary"
                    onClick={() => runStage('preprocess')}
                  >
                    Run preprocessing
                  </Button>
                  <Button onClick={skipPreprocess}>Skip to similarity</Button>
                </div>
              </div>
            )}
            {renderResult()}
            {stage.result && (
              <details className="match-flow-raw">
                <summary>Raw response</summary>
                <pre>{JSON.stringify(stage.result, null, 2)}</pre>
              </details>
            )}
          </main>
        </div>
        <div className="modal-footer match-flow-footer">
          <div>
            {accepted ? (
              <div className="match-flow-accepted">
                <strong>Accepted · {candidateLabel(accepted.candidate)}</strong>
                <span>
                  Matched at{' '}
                  {STEPS.find(({ key }) => key === accepted.step)?.label}
                  {accepted.confidence ? ` · ${accepted.confidence}` : ''}
                </span>
              </div>
            ) : (
              <span className="text-muted">
                No result accepted for this run.
              </span>
            )}
            {(runUsage.tokens > 0 || runUsage.cost > 0) && (
              <span className="match-flow-run-usage">
                Run total: {runUsage.tokens.toLocaleString()} tokens ·{' '}
                {formatCost(runUsage.cost)} estimated
              </span>
            )}
            {acceptError && (
              <p className="match-flow-accept-error">{acceptError}</p>
            )}
          </div>
          <div>
            {selectedCandidate && activeStep !== 'fingerprint' && (
              <Button
                color="success"
                disabled={isAccepting}
                onClick={() => acceptCandidate()}
              >
                {isAccepting && <Spinner size="sm" />}
                {isAccepting ? 'Matching…' : acceptButtonLabel}
              </Button>
            )}
            {activeStep !== 'image' &&
              activeIndex < STEPS.length - 1 &&
              stage.status !== 'processing' && (
                <Button color="primary" onClick={nextStep}>
                  Next Step
                </Button>
              )}
            <Button color="light" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={Boolean(enrichmentPreview)}
        toggle={closeEnrichmentPreview}
        className="match-flow-enrichment-dialog"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">Review enriched cigar</h5>
            <small className="text-muted">
              Confirm or correct the AI-provided details before adding this
              cigar.
            </small>
          </div>
          <button
            type="button"
            className="close"
            onClick={closeEnrichmentPreview}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            ×
          </button>
        </div>
        {enrichmentPreview && (
          <div className="modal-body match-flow-enrichment-form">
            <div className="match-flow-enrichment-source match-flow-enrichment-wide">
              <label>
                <span>Source URL</span>
                <input
                  className="form-control"
                  value={enrichmentPreview.sourceUrl}
                  disabled={reimportingEnrichment || savingEnrichment}
                  onChange={(event) =>
                    setEnrichmentPreview((current) =>
                      current
                        ? { ...current, sourceUrl: event.target.value }
                        : current
                    )
                  }
                />
              </label>
              <Button
                color="secondary"
                onClick={reimportEnrichment}
                disabled={reimportingEnrichment || savingEnrichment}
              >
                {reimportingEnrichment ? (
                  <>
                    <Spinner size="sm" /> Re-importing…
                  </>
                ) : (
                  'Re-import'
                )}
              </Button>
            </div>
            <label>
              <span>Brand</span>
              <AsyncCreatableSelect
                value={enrichmentPreview.brand}
                onChange={(brand) =>
                  setEnrichmentPreview((current) =>
                    current
                      ? { ...current, brand: brand as BrandOption | null }
                      : current
                  )
                }
                loadOptions={loadBrandOptions}
                defaultOptions
                isClearable
                isDisabled={savingEnrichment || reimportingEnrichment}
                placeholder="Search brands…"
              />
            </label>
            {[
              ['name', 'Name'],
              ['line', 'Line'],
              ['countryOfOrigin', 'Country of origin'],
              ['wrapper', 'Wrapper'],
              ['binder', 'Binder'],
              ['filler', 'Filler'],
              ['strength', 'Strength'],
              ['aliases', 'Aliases (comma separated)'],
            ].map(([field, label]) => (
              <label key={field}>
                <span>{label}</span>
                <input
                  className="form-control"
                  value={enrichmentPreview.data[field] || ''}
                  disabled={savingEnrichment || reimportingEnrichment}
                  onChange={(event) =>
                    setEnrichmentPreview((current) =>
                      current
                        ? {
                            ...current,
                            data: {
                              ...current.data,
                              [field]: event.target.value,
                            },
                          }
                        : current
                    )
                  }
                />
              </label>
            ))}
            <label className="match-flow-enrichment-wide">
              <span>Description</span>
              <textarea
                className="form-control"
                rows={4}
                value={enrichmentPreview.data.description || ''}
                disabled={savingEnrichment || reimportingEnrichment}
                onChange={(event) =>
                  setEnrichmentPreview((current) =>
                    current
                      ? {
                          ...current,
                          data: {
                            ...current.data,
                            description: event.target.value,
                          },
                        }
                      : current
                  )
                }
              />
            </label>
            {enrichmentError && (
              <div className="alert alert-danger match-flow-enrichment-wide mb-0">
                {enrichmentError}
              </div>
            )}
          </div>
        )}
        <div className="modal-footer">
          <Button
            color="light"
            onClick={closeEnrichmentPreview}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={saveEnrichedCigar}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            {savingEnrichment ? (
              <>
                <Spinner size="sm" /> Adding…
              </>
            ) : (
              'Add to catalog'
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default MatchFlowModal;
